import difference from 'lodash/difference';
import { nanoid } from 'nanoid';
import ToastEmitter from '../components/toaster/ToastEmmited';
import { getExtention } from '../utils/files';

// -----------------------------------------------------------------

type ExcelCell = {
  v: any;
  t: string;
  s: {
    fill: { fgColor: { rgb: string } };
    alignment: { horizontal: string; vertical: string };
    font: { bold: boolean; color: { rgb: string } };
  };
};

// ------------------------DOWNLOAD-----------------------

export const autofitColumns = (titles, worksheet) => {
  let objectMaxLength: any = [];

  for (const title of titles) {
    objectMaxLength = [...objectMaxLength, title.v.length + 2];
  }

  const wscols = objectMaxLength.map((w) => ({ width: w }));
  worksheet['!cols'] = wscols;
};

interface FileSettingsProps {
  filename: string;
  date?: string | null;
}

export const downloadExcel = async (
  dataRows: any[],
  exelSettings: any,
  { filename, date = null }: FileSettingsProps
) => {
  // Dynamically import xlsx-js-style
  const { utils: xlsxUtils, writeFile } = await import('xlsx-js-style');

  // STEP 1: Create a new workbook
  const workBook = xlsxUtils.book_new();

  // STEP 2: Create data rows and styles
  const titleColor = '212b36';
  const titleBgColor = 'c5c4e3';

  const titleStyle = {
    fill: { fgColor: { rgb: titleBgColor } },
    alignment: { horizontal: 'center', vertical: 'center' },
    font: { bold: true, color: { rgb: titleColor } },
  };

  let titles: ExcelCell[] = [];

  for (const title in exelSettings) {
    if (Object.prototype.hasOwnProperty.call(exelSettings, title)) {
      titles = [...titles, { v: exelSettings[title].title, t: 's', s: titleStyle }];
    }
  }

  const data = dataRows.map((row) => {
    const rows: { v: string; t: string; s: { alignment: { horizontal: string }; fill: any; font: any } }[] = [];

    for (const key in exelSettings) {
      if (!Object.prototype.hasOwnProperty.call(exelSettings, key)) {
        continue;
      }

      const rowValue = row[key];
      let value = '';

      if (rowValue !== undefined && rowValue !== null) {
        const inFormatter = exelSettings[key]?.inFormatter;
        value = inFormatter ? inFormatter(rowValue) : rowValue;
      }

      rows.push({
        v: value,
        t: 's',
        s: { alignment: { horizontal: exelSettings[key]?.align || 'left' } } as any,
      });
    }

    return rows;
  });

  // colorize Data
  const dataColor = '212b36';

  const oddRowBgColor = 'f0edf4';
  const evenRowBgColor = 'ffffff';

  for (const [y, row] of data.entries()) {
    // We use eslind disable bcs we needs second array member col
    // eslint-disable-next-line
    for (const [x, col] of row.entries()) {
      col.s.fill = {
        fgColor: { rgb: y % 2 === 0 ? evenRowBgColor : oddRowBgColor },
      };
      col.s.font = { color: { rgb: dataColor } };
    }
  }

  // STEP 3: Create worksheet with rows; Add worksheet to workbook
  const workSheet = xlsxUtils.aoa_to_sheet([titles, ...data]);

  autofitColumns(titles, workSheet);
  xlsxUtils.book_append_sheet(workBook, workSheet, filename);

  // STEP 4: Write Excel file to browser
  writeFile(workBook, `${filename}${date ? `_${date}` : ''}.xlsx`);
};

// ------------------------IMPORT-----------------------
export const convertToJson = (headers, data, exelSettings, validateSchema) => {
  const rows: { _id: string; _errors: Record<string, string[]> }[] = [];
  // const errors = [];

  const keys = {};
  for (const header of headers) {
    for (const key in exelSettings) {
      if (!Object.prototype.hasOwnProperty.call(exelSettings, key)) {
        continue;
      }

      const exelTitle = exelSettings[key].title;
      if (exelTitle?.trim().toLowerCase() === header?.trim().toLowerCase()) {
        keys[header] = key;
        break;
      }
    }
  }

  const exelSettingsTitles = Object.keys(exelSettings).map((key) => exelSettings[key].title);

  const diff = difference(headers, exelSettingsTitles).map((el) => (el === '' || !el ? 'Empty' : el));

  data.forEach((row) => {
    const rowData: { _id: string; _errors: Record<string, string[]> } = {
      _id: nanoid(),
      _errors: {},
    };
    row.forEach((element, index) => {
      const key = keys[headers[index]];

      const rowValue = element;
      let value = null;

      if (rowValue) {
        const outFormatter = exelSettings[key]?.outFormatter || null;
        value = outFormatter ? outFormatter(rowValue) : rowValue;
      }

      rowData[key] = value;
    });

    // Code that finds errors based on yup validateSchema
    try {
      validateSchema.validateSync(rowData, { abortEarly: false });
    } catch (err: any) {
      if (err?.inner) {
        for (const inner of err?.inner) {
          const { path, message } = inner;

          rowData._errors[path] =
            rowData._errors[path]?.length > 0
              ? (rowData._errors[path] = [...rowData._errors[path], message])
              : (rowData._errors[path] = [message]);
        }
      }
    }

    if (diff) {
      rowData._errors._unsupportedHeaders = diff;
    }

    rows.push(rowData);
  });

  return rows;
};

export const importExcel = async (e, exelSettings, validateSchema, extensions, callback) => {
  // Dynamically import xlsx-js-style
  const { read, utils: xlsxUtils } = await import('xlsx-js-style');

  const file = e.target.files[0];

  const reader = new FileReader();

  reader.onload = (event) => {
    const bstr = event.target !== null ? event.target.result : null;
    const workBook = read(bstr, { type: 'binary' });

    // GET FIRST SHEET
    const workSheetName = workBook.SheetNames[0];
    const workSheet = workBook.Sheets[workSheetName];
    // CONVERT SHEET TO ARRAY
    const fileData = xlsxUtils.sheet_to_json(workSheet, { header: 1 });
    const headers = fileData[0];

    // removing header
    fileData.splice(0, 1);

    const json = convertToJson(headers, fileData, exelSettings, validateSchema);

    callback(json);
  };

  if (file) {
    if (getExtention(file, extensions)) {
      reader.readAsBinaryString(file);
    } else {
      ToastEmitter.notify('Invalid file input, Select Excel, CSV file', 'error');
    }
  }
};

export const xlsxToJson = async (file, callback, options) => {
  // Dynamically import xlsx-js-style
  const { read, utils: xlsxUtils } = await import('xlsx-js-style');

  const reader = new FileReader();

  reader.onload = (event) => {
    const data = event.target !== null ? event.target.result : null;
    const workBook = read(data, { type: 'binary' });

    const sheetsJson: { name: string; data: unknown[] }[] = [];

    for (const sheetName of workBook.SheetNames) {
      const jsonObject = xlsxUtils.sheet_to_json(workBook.Sheets[sheetName], options);

      sheetsJson.push({
        name: sheetName,
        data: jsonObject,
      });
    }

    callback(sheetsJson);
  };

  reader.readAsBinaryString(file);
};
